import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { NoData } from "@/components/ui/no-data";
import * as Sentry from "@sentry/react";
import { PhoneInput } from "@/components/ui/phone-input";
import { PhoneNumberCopy } from "@/components/ui/phone-number-copy";
import { PhoneNumberDisplay } from "@/components/ui/phone-number-display";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { StatWithTooltip } from "@/components/ui/stat-with-tooltip";
import { formatDate } from "@/lib/dateFormatter";
import { ScheduledCall } from "@/models/ScheduledCall";
import { leadService } from "@/services/leadService";
import { useDebounce } from "@/hooks/useDebounce";
import { RefreshCw } from "lucide-react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Calendar,
  Clock,
  Mail,
  MailX,
  Pencil,
  Phone,
  Plus,
  Search,
  TrendingUp,
  Trash2,
  UserRound,
  Users,
  Video,
  Copy,
  Link,
} from "lucide-react";

import { forwardRef, useEffect, useImperativeHandle, useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../supabase/auth";
import { supabase } from "../../../supabase/supabase";
import { useOrganization } from "../../App";
import { useBackendFlags } from "@/hooks/useBackendFlags";
import { apiServerHeaders } from "@/lib/apiServerHeaders";
import { BackendPicker } from "@/components/shared/BackendPicker";
import { ConfirmCallDialog } from "../campaigns/ConfirmCallDialog";
import { CancelLeadCallDialog } from "./CancelLeadCallDialog";
import { ScheduleLeadCallDialog } from "./ScheduleLeadCallDialog";
import { WhatsAppMessageDialog } from "./WhatsAppMessageDialog";
import { CallErrorDialog } from "./CallErrorDialog";
import { MessageSquare } from "lucide-react";
import type { OutboundCallBackend } from "@/services/outboundCallService";

interface Lead {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  created_at: string;
}

interface LeadWithScheduledCall extends Lead {
  scheduledCall?: ScheduledCall;
  totalCalls?: number;
  lastCalledAt?: string | null;
}

export interface LeadsListRef {
  openCreateDialog: () => void;
}

const LeadsList = forwardRef<LeadsListRef>((props, ref) => {
  // Function to open schedule dialog
  const openScheduleDialog = (
    lead: LeadWithScheduledCall,
    mode: "schedule" | "reschedule"
  ) => {
    setSelectedLead(lead);
    setScheduleMode(mode);
    setIsScheduleDialogOpen(true);
  };

  // Function to open cancel dialog
  const openCancelDialog = (lead: LeadWithScheduledCall) => {
    setSelectedLead(lead);
    setIsCancelDialogOpen(true);
  };
  const [leads, setLeads] = useState<LeadWithScheduledCall[]>([]);
  const [stats, setStats] = useState({
    totalLeads: 0,
    newLeadsThisMonth: 0,
    leadsWithEmail: 0,
    leadsWithoutEmail: 0,
    scheduledLeads: 0,
  });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get("search") ?? "";
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [localSearchValue, setLocalSearchValue] = useState(initialSearch);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [sortField, setSortField] = useState<
    "name" | "created_at" | "scheduled_time" | "total_calls" | "last_called_at"
  >("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [showScheduledOnly, setShowScheduledOnly] = useState(
    () => searchParams.get("scheduledOnly") === "true"
  );
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false,
    method: 'cursor' as 'cursor' | 'offset',
    nextCursor: undefined as string | undefined,
    currentCursor: undefined as string | undefined,
    estimatedTotal: undefined as number | undefined
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isCallDialogOpen, setIsCallDialogOpen] = useState(false);
  const [isConfirmCallDialogOpen, setIsConfirmCallDialogOpen] = useState(false);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isWhatsAppDialogOpen, setIsWhatsAppDialogOpen] = useState(false);
  const [isCallErrorDialogOpen, setIsCallErrorDialogOpen] = useState(false);
  const [callErrorMessage, setCallErrorMessage] = useState<string>("");
  const [selectedCallBackend, setSelectedCallBackend] =
    useState<OutboundCallBackend>("v1");
  const [startLanguage, setStartLanguage] = useState("");
  const [scheduleMode, setScheduleMode] = useState<"schedule" | "reschedule">(
    "schedule"
  );
  const [leadToCall, setLeadToCall] = useState<{
    name: string;
    phoneNumber: string;
  } | null>(null);
  const [selectedLead, setSelectedLead] =
    useState<LeadWithScheduledCall | null>(null);
  const [currentLead, setCurrentLead] = useState<Lead | null>(null);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
  });

  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Get the current organization from context
  const { currentOrganization } = useOrganization();
  // Call backends this organization has (V1 voice, V2 voice, V1 video)
  const { defaultBackend, videoV1 } = useBackendFlags();

  // localStorage helpers for persisting last selected assistant
  const LAST_ASSISTANT_KEY = 'voxi_last_assistant';

  const getLastAssistantId = () => {
    const orgId = currentOrganization?.id;
    if (!orgId) return null;
    try {
      const stored = localStorage.getItem(`${LAST_ASSISTANT_KEY}_${orgId}`);
      if (stored) {
        const { assistantId } = JSON.parse(stored);
        return assistantId;
      }
    } catch {
      // Ignore parse errors
    }
    return null;
  };

  const saveLastAssistantId = (assistantId: string | null) => {
    const orgId = currentOrganization?.id;
    if (!orgId || !assistantId) return;
    localStorage.setItem(
      `${LAST_ASSISTANT_KEY}_${orgId}`,
      JSON.stringify({ assistantId, timestamp: Date.now() })
    );
  };

  // Expose methods through ref
  useImperativeHandle(ref, () => ({
    openCreateDialog
  }));

  useEffect(() => {
    if (currentOrganization) {
      fetchLeads();
    }
  }, [currentOrganization]);

  // Handle search query and filter changes (debounced)
  useEffect(() => {
    if (currentOrganization) {
      // Reset pagination and fetch fresh data when search or filters change
      setPagination(prev => ({
        ...prev,
        offset: 0,
        nextCursor: undefined,
        currentCursor: undefined
      }));
      fetchLeads();
    }
  }, [debouncedSearchQuery, showScheduledOnly]);

  const handleSearchChange = (value: string) => {
    setLocalSearchValue(value);
    setSearchQuery(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set("search", value);
        else next.delete("search");
        return next;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    const q = searchParams.get("search") ?? "";
    const scheduledOnly = searchParams.get("scheduledOnly") === "true";
    setSearchQuery(q);
    setLocalSearchValue(q);
    setShowScheduledOnly(scheduledOnly);
  }, [searchParams]);

  useEffect(() => {
    setLocalSearchValue(searchQuery);
  }, [searchQuery]);

  const fetchLeads = async (isBackgroundRefresh = false, append = false) => {
    if (!currentOrganization) {
      setLoading(false);
      return;
    }

    try {
      if (append) {
        setLoadingMore(true);
      } else if (isBackgroundRefresh) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }

      const cursor = append ? pagination.nextCursor : undefined;
      
      const response = await leadService.getLeadsWithPagination({
        organizationId: currentOrganization.id,
        limit: pagination.limit,
        cursor: cursor,
        method: pagination.method,
        searchQuery: searchQuery,
        showScheduledOnly: showScheduledOnly
      });

      const leadsData = response.data || [];

      // Get all scheduled calls for the organization
      const scheduledCalls = await leadService.getAllScheduledCalls(
        currentOrganization.id
      );

      // Get call counts and last called at for all leads - but limit to current leads for performance
      const leadIds = leadsData.map(lead => lead.id);
      let callLogsData: any[] = [];
      
      if (leadIds.length > 0) {
        const { data } = await supabase
          .from("call_logs")
          .select("lead_id, created_at")
          .eq("organization_id", currentOrganization.id)
          .in("lead_id", leadIds)
          .order("created_at", { ascending: false });
        
        callLogsData = data || [];
      }

      // Count calls per lead and get last called at
      const callCounts: Record<string, number> = {};
      const lastCalledAt: Record<string, string> = {};

      if (callLogsData) {
        callLogsData.forEach((call) => {
          if (call.lead_id) {
            // Count calls
            callCounts[call.lead_id] = (callCounts[call.lead_id] || 0) + 1;

            // Track the latest call (first one due to ordering)
            if (!lastCalledAt[call.lead_id]) {
              lastCalledAt[call.lead_id] = call.created_at;
            }
          }
        });
      }

      // Merge leads with their scheduled calls, call counts, and last called at
      const leadsWithScheduledCalls = leadsData.map((lead) => {
        const scheduledCall = scheduledCalls.find(
          (call) => call.lead_id === lead.id
        );
        return {
          ...lead,
          scheduledCall: scheduledCall,
          totalCalls: callCounts[lead.id] || 0,
          lastCalledAt: lastCalledAt[lead.id] || null,
        };
      });

      // Update leads state
      if (append) {
        setLeads(prevLeads => [...prevLeads, ...leadsWithScheduledCalls]);
      } else {
        setLeads(leadsWithScheduledCalls);
      }

      // Update pagination state
      setPagination(prev => ({
        ...prev,
        ...response.pagination,
        total: response.pagination.total || prev.total,
        estimatedTotal: response.pagination.estimatedTotal
      }));

      // Calculate stats only on initial load or refresh (not append)
      if (!append) {
        const now = new Date();
        const firstDayOfMonth = new Date(
          now.getFullYear(),
          now.getMonth(),
          1
        ).toISOString();
        const orgId = currentOrganization.id;

        const [
          { count: totalLeads },
          { count: newLeadsThisMonth },
          { count: leadsWithEmail },
        ] = await Promise.all([
          supabase
            .from("leads")
            .select("*", { count: "exact", head: true })
            .eq("organization_id", orgId),
          supabase
            .from("leads")
            .select("*", { count: "exact", head: true })
            .eq("organization_id", orgId)
            .gte("created_at", firstDayOfMonth),
          supabase
            .from("leads")
            .select("*", { count: "exact", head: true })
            .eq("organization_id", orgId)
            .not("email", "is", null)
            .neq("email", ""),
        ]);

        setStats({
          totalLeads: totalLeads ?? 0,
          newLeadsThisMonth: newLeadsThisMonth ?? 0,
          leadsWithEmail: leadsWithEmail ?? 0,
          leadsWithoutEmail: (totalLeads ?? 0) - (leadsWithEmail ?? 0),
          scheduledLeads: scheduledCalls.length,
        });
      }
    } catch (error) {
      Sentry.logger.error('Error fetching leads', { 
        error,
        log_source: 'leads_list',
        organization_id: currentOrganization?.id,
        is_append: append,
        search_query: searchQuery
      });
      toast({
        title: "Error",
        description: "Failed to fetch leads. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setIsRefreshing(false);
    }
  };

  const validatePhoneNumber = (phone: string) => {
    // Basic phone validation - can be enhanced based on requirements
    const phoneRegex = /^\+?[0-9\s\-\(\)]{8,20}$/;
    return phoneRegex.test(phone);
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const trimmedFirstName = formData.first_name.trim();
    const trimmedLastName = formData.last_name.trim();
    const trimmedPhoneNumber = formData.phone_number.trim();
    const trimmedEmail = formData.email.trim();

    // Update form data with trimmed values
    setFormData({
      ...formData,
      first_name: trimmedFirstName,
      last_name: trimmedLastName,
      phone_number: trimmedPhoneNumber,
      email: trimmedEmail,
    });

    if (!trimmedFirstName) {
      toast({
        title: "Missing Information",
        description: "First name is required.",
        variant: "destructive",
      });
      return false;
    }

    if (!trimmedLastName) {
      toast({
        title: "Missing Information",
        description: "Last name is required.",
        variant: "destructive",
      });
      return false;
    }

    if (!trimmedEmail) {
      toast({
        title: "Missing Information",
        description: "Email is required.",
        variant: "destructive",
      });
      return false;
    }

    if (!trimmedPhoneNumber) {
      toast({
        title: "Missing Information",
        description: "Phone number is required.",
        variant: "destructive",
      });
      return false;
    }

    if (!validatePhoneNumber(trimmedPhoneNumber)) {
      toast({
        title: "Invalid Format",
        description: "Please enter a valid phone number.",
        variant: "destructive",
      });
      return false;
    }

    if (!validateEmail(trimmedEmail)) {
      toast({
        title: "Invalid Format",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleCreateLead = async () => {
    try {
      if (!validateForm()) {
        return;
      }

      if (!currentOrganization) {
        toast({
          title: "Error",
          description:
            "Organization information is missing. Please refresh the page.",
          variant: "destructive",
        });
        return;
      }

      if (!user) {
        toast({
          title: "Error",
          description: "User information is missing. Please sign in again.",
          variant: "destructive",
        });
        return;
      }

      setIsSaving(true);
      const { data, error } = await supabase.from("leads").insert([
        {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone_number: formData.phone_number,
          created_by: user.id,
          organization_id: currentOrganization.id,
        },
      ]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Lead created successfully.",
      });

      setIsCreateDialogOpen(false);
      resetForm();
      fetchLeads(true);
    } catch (error) {
      Sentry.logger.error('Error creating lead', { 
        error,
        log_source: 'leads_list',
        organization_id: currentOrganization?.id,
        lead_first_name: formData.first_name,
        lead_last_name: formData.last_name
      });
      
      if (
        (error as any)?.code === "23505" ||
        ((error as any)?.message?.includes("duplicate key") &&
          (error as any)?.message?.includes("phone_number"))
      ) {
        toast({
          title: "Duplicate Phone Number",
          description: "A lead with this phone number already exists in your organization.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to create lead. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateLead = async () => {
    try {
      if (!currentLead) return;
      if (!validateForm()) {
        return;
      }

      setIsSaving(true);
      const { data, error } = await supabase
        .from("leads")
        .update({
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone_number: formData.phone_number,
          updated_at: new Date().toISOString(),
        })
        .eq("id", currentLead.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Lead updated successfully.",
      });

      setIsEditDialogOpen(false);
      resetForm();
      fetchLeads(true);
    } catch (error) {
      Sentry.logger.error('Error updating lead', { 
        error,
        log_source: 'leads_list',
        lead_id: currentLead?.id,
        organization_id: currentOrganization?.id
      });
      
      if (
        (error as any)?.code === "23505" ||
        ((error as any)?.message?.includes("duplicate key") &&
          (error as any)?.message?.includes("phone_number"))
      ) {
        toast({
          title: "Duplicate Phone Number",
          description: "A lead with this phone number already exists in your organization.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to update lead. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteLead = async () => {
    try {
      if (!currentLead) return;

      setIsDeleting(true);
      const { data, error } = await supabase
        .from("leads")
        .delete()
        .eq("id", currentLead.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Lead deleted successfully.",
      });

      setIsDeleteDialogOpen(false);
      fetchLeads(true);
    } catch (error) {
      Sentry.logger.error('Error deleting lead', { 
        error,
        log_source: 'leads_list',
        lead_id: currentLead?.id,
        organization_id: currentOrganization?.id
      });
      toast({
        title: "Error",
        description: "Failed to delete lead. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // State for assistant selection
  const [selectedAssistantId, setSelectedAssistantId] = useState<string | null>(
    null
  );
  const [shouldRestoreLastAssistant, setShouldRestoreLastAssistant] = useState(false);
  const [assistants, setAssistants] = useState<
    Array<{ id: string; name: string; variables?: string[] }>
  >([]);
  const [loadingAssistants, setLoadingAssistants] = useState(false);
  const [variableValues, setVariableValues] = useState<Record<string, string>>(
    {}
  );
  const [initiatingCall, setInitiatingCall] = useState(false);
  const [isVideoCall, setIsVideoCall] = useState(false);
  
  // State for video call dialog steps
  const [dialogStep, setDialogStep] = useState<'assistant' | 'monitoring' | 'link'>('assistant');
  const [tabChangeDetection, setTabChangeDetection] = useState(false);
  const [externalMonitorDetection, setExternalMonitorDetection] = useState(false);
  const [enableScreenSharing, setEnableScreenSharing] = useState(false);
  const [enableIntermittentFaceOutOfFocus, setEnableIntermittentFaceOutOfFocus] = useState(false);
  const [generatedVideoLink, setGeneratedVideoLink] = useState<string>("");
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [startTime, setStartTime] = useState<string>("");

  // State for phone number selection
  const [selectedPhoneNumberId, setSelectedPhoneNumberId] = useState<
    string | null
  >(null);
  const [phoneNumbers, setPhoneNumbers] = useState<
    Array<{ id: string; phone_number: string; is_default?: boolean }>
  >([]);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);

  // Fetch assistants for the dropdown
  const fetchAssistants = async () => {
    setLoadingAssistants(true);
    try {
      const { data, error } = await supabase
        .from("assistants")
        .select("id, name, variables")
        .order("name");

      if (error) throw error;
      setAssistants(data || []);
    } catch (error) {
      Sentry.logger.error('Error fetching assistants', { 
        error,
        log_source: 'leads_list',
        organization_id: currentOrganization?.id
      });
      toast({
        title: "Error",
        description: "Failed to load assistants. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingAssistants(false);
    }
  };

  // Effect to restore last selected assistant when assistants are loaded
  useEffect(() => {
    if (!loadingAssistants && shouldRestoreLastAssistant && assistants.length > 0) {
      const lastAssistantId = getLastAssistantId();
      if (lastAssistantId && assistants.some(a => a.id === lastAssistantId)) {
        setSelectedAssistantId(lastAssistantId);
        // Initialize variables for the pre-selected assistant
        if (leadToCall) {
          const currentLead = leads.find(
            (lead) =>
              `${lead.first_name} ${lead.last_name}` === leadToCall.name &&
              lead.phone_number === leadToCall.phoneNumber
          );
          if (currentLead) {
            initializeVariableValues(currentLead, lastAssistantId);
          }
        }
      }
      setShouldRestoreLastAssistant(false);
    }
  }, [loadingAssistants, shouldRestoreLastAssistant, assistants]);

  // Get the selected assistant's variables
  const getSelectedAssistantVariables = () => {
    if (!selectedAssistantId) return [];
    const assistant = assistants.find((a) => a.id === selectedAssistantId);
    return assistant?.variables || [];
  };

  // Initialize variable values when a lead is selected and an assistant is chosen
  const initializeVariableValues = (
    lead: Lead,
    assistantId?: string | null
  ) => {
    try {
      // If assistantId is provided, use it for the lookup, otherwise use selected assistant
      const assistant = assistantId
        ? assistants.find((a) => a.id === assistantId)
        : assistants.find((a) => a.id === selectedAssistantId);

      const variables = assistant?.variables || [];
      const initialValues: Record<string, string> = {};

      if (!variables || variables.length === 0) {
        setVariableValues({});
        return;
      }

      variables.forEach((variable) => {
        if (!variable) return;

        // Ensure we have a string to work with
        const variableName = String(variable);

        // Map variable names to lead properties
        try {
          switch (variableName.toLowerCase()) {
            case "first_name":
              initialValues[variableName] = lead?.first_name || "";
              break;
            case "last_name":
              initialValues[variableName] = lead?.last_name || "";
              break;
            case "name":
            case "lead_contact_name":
              initialValues[variableName] = lead
                ? `${lead.first_name || ""} ${lead.last_name || ""}`.trim()
                : "";
              break;
            case "email":
              initialValues[variableName] = lead?.email || "";
              break;
            case "phone":
            case "phone_number":
            case "lead_contact_number":
              initialValues[variableName] = lead?.phone_number || "";
              break;
            default:
              initialValues[variableName] = "";
          }
        } catch (err) {
          Sentry.logger.error('Error mapping variable', { 
            error: err,
            log_source: 'leads_list',
            variable_name: variableName,
            lead_id: lead?.id
          });
          initialValues[variableName] = ""; // Default to empty string on error
        }
      });

      setVariableValues(initialValues);
    } catch (error) {
      Sentry.logger.error('Error initializing variable values', { 
        error,
        log_source: 'leads_list',
        lead_id: lead?.id,
        assistant_id: assistantId || selectedAssistantId
      });
      setVariableValues({}); // Set empty object on error
    }
  };

  // Fetch outbound phone numbers for the dropdown
  const fetchPhoneNumbers = async () => {
    setLoadingPhoneNumbers(true);
    try {
      const { data, error } = await supabase
        .from("phone_numbers")
        .select("id, phone_number, is_default")
        .eq("type", "outbound")
        .order("is_default", { ascending: false }) // Default numbers first
        .order("phone_number");

      if (error) throw error;

      // Set the default phone number if available
      const phoneNumbersData = data || [];
      setPhoneNumbers(phoneNumbersData);

      // Select the default phone number if available
      const defaultNumber = phoneNumbersData.find(
        (pn) => pn.is_default === true
      );
      if (defaultNumber) {
        setSelectedPhoneNumberId(defaultNumber.id);
      } else if (phoneNumbersData.length > 0) {
        setSelectedPhoneNumberId(phoneNumbersData[0].id);
      }
    } catch (error) {
      Sentry.logger.error('Error fetching phone numbers', { 
        error,
        log_source: 'leads_list',
        organization_id: currentOrganization?.id
      });
      toast({
        title: "Error",
        description: "Failed to load outbound phone numbers. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingPhoneNumbers(false);
    }
  };

  const openCallDialog = (lead: Lead, video: boolean = false) => {
    setLeadToCall({
      name: `${lead.first_name} ${lead.last_name}`,
      phoneNumber: lead.phone_number,
    });

    // Set video call flag
    setIsVideoCall(video);
    // Video interviews run on V1; a voice call starts on the organization's default backend
    setSelectedCallBackend(video ? "v1" : defaultBackend);
    setStartLanguage("");

    // Reset dialog state for video calls
    if (video) {
      setDialogStep('assistant');
      setTabChangeDetection(false);
      setExternalMonitorDetection(false);
    }

    // Reset assistant selection and set flag to restore last selected
    setSelectedAssistantId(null);
    setShouldRestoreLastAssistant(true);

    // Fetch assistants and phone numbers
    fetchAssistants();
    fetchPhoneNumbers();

    // Don't initialize variables yet since no assistant is selected
    // We'll initialize them when an assistant is selected (or restored from localStorage)
    setVariableValues({});
    setIsCallDialogOpen(true);
  };

  // Function to directly initiate a call with confirmation
  const initiateCallWithConfirmation = async (
    lead: Lead,
    assistantId: string,
    assistantName: string,
    video: boolean = false
  ) => {
    // Set video call flag
    setIsVideoCall(video);
    if (!currentOrganization) {
      toast({
        title: "Error",
        description:
          "Organization information is missing. Please refresh the page.",
        variant: "destructive",
      });
      return;
    }

    setLeadToCall({
      name: `${lead.first_name} ${lead.last_name}`,
      phoneNumber: lead.phone_number,
    });

    // Fetch phone numbers for outbound calls
    await fetchPhoneNumbers();

    // Set the assistant ID
    setSelectedAssistantId(assistantId);
    setStartLanguage("");

    // Initialize variables for this lead and assistant
    initializeVariableValues(lead, assistantId);

    // Show confirmation dialog
    setIsConfirmCallDialogOpen(true);
  };

  const generateVideoCallLink = async () => {
    if (!leadToCall || !selectedAssistantId) return;

    // Find the lead ID
    const currentLead = leads.find(
      (lead) =>
        `${lead.first_name} ${lead.last_name}` === leadToCall.name &&
        lead.phone_number === leadToCall.phoneNumber
    );

    if (!currentLead?.id) return;

    setIsGeneratingLink(true);
    try {
      // Call the backend API to generate a short link
      const CALL_SERVICE_URL = import.meta.env.VITE_CALL_SERVICE_URL || 'http://localhost:8000';
      const response = await fetch(`${CALL_SERVICE_URL}/api/video-link`, {
        method: 'POST',
        headers: await apiServerHeaders(),
        body: JSON.stringify({
          lead_id: currentLead.id,
          agent_id: selectedAssistantId,
          proctoring_tools: {
            tab_change_detection: tabChangeDetection,
            screen_share: enableScreenSharing,
            multiple_monitor_detection: externalMonitorDetection,
            face_out_of_view: enableIntermittentFaceOutOfFocus,
            face_blur_detection: enableIntermittentFaceOutOfFocus,
          },
          organization_id: currentOrganization?.id,
          start_time: startTime ? new Date(startTime).toISOString() : null,
          echo_cancellation: true,
          metadata: {
            variables: variableValues,
            generated_by: user?.id,
            generated_at: new Date().toISOString(),
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate video link');
      }

      const data = await response.json();
      // Construct the full URL using the current domain and the token
      const currentDomain = window.location.origin;
      const fullVideoCallUrl = `${currentDomain}/video-call/${data.token}`;
      setGeneratedVideoLink(fullVideoCallUrl);
      setDialogStep('link');
      
      toast({
        title: "Success",
        description: "Video interview link generated successfully!",
      });
    } catch (error) {
      Sentry.logger.error('Error generating video link', { 
        error,
        log_source: 'leads_list',
        lead_id: currentLead?.id,
        assistant_id: selectedAssistantId,
        organization_id: currentOrganization?.id
      });
      toast({
        title: "Error",
        description: "Failed to generate video link. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const handleCall = async () => {
    if (!leadToCall) return;

    // Manual phone call option
    if (!selectedAssistantId) {
      window.open(`tel:${leadToCall.phoneNumber}`, "_blank");
      toast({
        title: "Opening Phone Dialer",
        description: `Calling ${leadToCall.name}...`,
      });
      setIsCallDialogOpen(false);
      return;
    }

    // Find the lead ID
    const currentLead = leads.find(
      (lead) =>
        `${lead.first_name} ${lead.last_name}` === leadToCall.name &&
        lead.phone_number === leadToCall.phoneNumber
    );

    // If it's a video call and we're in the link step, generate the link
    if (isVideoCall && dialogStep === 'link') {
      try {
        await generateVideoCallLink();
        return;
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to generate video call link.",
          variant: "destructive",
        });
        return;
      }
    }

    // If it's a video call, navigate to video call page with parameters
    if (isVideoCall) {
      if (!currentLead?.id) {
        toast({
          title: "Error",
          description: "Lead not found. Please try again.",
          variant: "destructive",
        });
        return;
      }

      // Build URL with query parameters
      const params = new URLSearchParams({
        assistantId: selectedAssistantId,
      });

      // Add variable values if any
      if (Object.keys(variableValues).length > 0) {
        params.append("variables", JSON.stringify(variableValues));
      }

      // Add monitoring options
      params.append("tabChangeDetection", tabChangeDetection.toString());
      params.append("externalMonitorDetection", externalMonitorDetection.toString());
      params.append("screenSharing", enableScreenSharing.toString());
      params.append("blurDetection", enableIntermittentFaceOutOfFocus.toString());

      // Navigate to video call page with parameters
      navigate(`/video-call/${currentLead.id}?${params.toString()}`);
      
      setIsCallDialogOpen(false);
      setSelectedAssistantId(null);
      setVariableValues({});
      setDialogStep('assistant');
      setTabChangeDetection(false);
      setExternalMonitorDetection(false);
      setEnableScreenSharing(false);
      setEnableIntermittentFaceOutOfFocus(false);
      setStartTime("");
      return;
    }

    // Check if there's a phone number selected for outbound calls
    if (
      selectedCallBackend === "v1" &&
      !selectedPhoneNumberId &&
      selectedAssistantId
    ) {
      toast({
        title: "Phone Number Required",
        description:
          "Please select an outbound phone number to use for this call.",
        variant: "destructive",
      });
      return;
    }

    // Check if organization is available
    if (!currentOrganization) {
      toast({
        title: "Organization Required",
        description:
          "Organization information is missing. Please refresh the page.",
        variant: "destructive",
      });
      return;
    }

    // Automated call with AI agent
    setInitiatingCall(true);
    try {
      // Import outboundCallService dynamically to avoid potential circular dependencies
      const outboundCallService = (
        await import("../../services/outboundCallService")
      ).default;

      let callResult;

      // Check if we have a lead ID (existing lead) or need to create a new one
      if (currentLead?.id) {
        // Use existing lead
        callResult = await outboundCallService.initiateCallToExistingLead(
          currentLead.id,
          selectedAssistantId,
          selectedPhoneNumberId,
          currentOrganization.id,
          variableValues,
          undefined, // campaignId
          isVideoCall, // Pass the video flag
          { backend: selectedCallBackend, startLanguage }
        );
      } else {
        // Need to create a new lead
        const nameParts = leadToCall.name.split(" ");
        const firstName = nameParts[0] || "Unknown";
        const lastName = nameParts.slice(1).join(" ") || "User";

        callResult = await outboundCallService.initiateCallToNewLead(
          {
            first_name: firstName,
            last_name: lastName,
            phone_number: leadToCall.phoneNumber,
            // Add any other lead information you have
          },
          selectedAssistantId,
          selectedPhoneNumberId,
          currentOrganization.id,
          variableValues,
          isVideoCall,
          { backend: selectedCallBackend, startLanguage }
        );
      }

      if (callResult && callResult.success) {
        toast({
          title: "Call Initiated",
          description: `AI assistant is calling ${leadToCall.name}...`,
        });
      } else {
        toast({
          title: "Call Setup Required",
          description:
            (callResult && callResult.message) ||
            "Call service not fully configured",
          variant: "destructive",
        });
        if (callResult && callResult.instructions) {
          Sentry.logger.info('Call setup instructions received', { 
            log_source: 'leads_list',
            instructions: callResult.instructions
          });
        }
      }
    } catch (error) {
      Sentry.logger.error('Error initiating call', { 
        error,
        log_source: 'leads_list',
        lead_id: currentLead?.id,
        assistant_id: selectedAssistantId,
        phone_number_id: selectedPhoneNumberId,
        is_video_call: isVideoCall
      });
      
      // Extract detailed error message
      let errorMessage = "Failed to initiate the call. Please try again.";
      if (error instanceof Error && error.message) {
        errorMessage = error.message;
      }
      
      // Show detailed error dialog for prerequisite errors, toast for others
      if (errorMessage.includes("Missing requirements:")) {
        setCallErrorMessage(errorMessage);
        setIsCallErrorDialogOpen(true);
      } else {
        toast({
          title: "Call Failed",
          description: errorMessage,
          variant: "destructive",
          duration: 8000, // Show longer for detailed error messages
        });
      }
    } finally {
      setInitiatingCall(false);
      setIsCallDialogOpen(false);
      setSelectedAssistantId(null);
      setSelectedPhoneNumberId(null);
      setSelectedCallBackend(defaultBackend);
      setStartLanguage("");
      setVariableValues({});
    }
  };

  const openEditDialog = (lead: Lead) => {
    setCurrentLead(lead);
    setFormData({
      first_name: lead.first_name,
      last_name: lead.last_name,
      email: lead.email || "",
      phone_number: lead.phone_number,
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (lead: Lead) => {
    setCurrentLead(lead);
    setIsDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
    });
    setCurrentLead(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsCreateDialogOpen(true);
  };

  const handleCreateDialogClose = (open: boolean) => {
    setIsCreateDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const handleEditDialogClose = (open: boolean) => {
    setIsEditDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  // Since filtering is now done server-side, we only need to sort the leads
  const filteredLeads = leads
    .sort((a, b) => {
      let comparison = 0;

      if (sortField === "name") {
        const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        comparison = nameA.localeCompare(nameB);
      } else if (sortField === "created_at") {
        comparison =
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      } else if (sortField === "scheduled_time") {
        // Handle scheduled time sorting
        const timeA = a.scheduledCall?.scheduled_time
          ? new Date(a.scheduledCall.scheduled_time).getTime()
          : 0;
        const timeB = b.scheduledCall?.scheduled_time
          ? new Date(b.scheduledCall.scheduled_time).getTime()
          : 0;

        // Leads without scheduled calls go to the end when sorting by scheduled time
        if (timeA === 0 && timeB === 0) return 0;
        if (timeA === 0) return 1;
        if (timeB === 0) return -1;

        comparison = timeA - timeB;
      } else if (sortField === "total_calls") {
        comparison = (a.totalCalls || 0) - (b.totalCalls || 0);
      } else if (sortField === "last_called_at") {
        // Handle last called at sorting
        const timeA = a.lastCalledAt ? new Date(a.lastCalledAt).getTime() : 0;
        const timeB = b.lastCalledAt ? new Date(b.lastCalledAt).getTime() : 0;

        // Leads without calls go to the end when sorting by last called at
        if (timeA === 0 && timeB === 0) return 0;
        if (timeA === 0) return 1;
        if (timeB === 0) return -1;

        comparison = timeA - timeB;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

  // Expose the openCreateDialog method via ref
  useImperativeHandle(ref, () => ({
    openCreateDialog: () => {
      setIsCreateDialogOpen(true);
    }
  }));

  return (
    <div className="w-full py-6 bg-background text-foreground">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
        <div className="relative bg-secondary/20 p-6 rounded-lg shadow-sm transition-all duration-300 hover:shadow-md hover:scale-105 hover:-translate-y-1 border border-border-border group">
          <StatWithTooltip
            value={stats.totalLeads.toString()}
            label="Total Leads"
            tooltip="Total number of leads in the system"
            valueClassName="text-2xl font-bold text-foreground dark:text-ink-faint"
            icon={<Users className="w-5 h-5 text-foreground" />}
          />
        </div>

        <div className="relative bg-good-bg-good-bg/20 p-6 rounded-lg shadow-sm transition-all duration-300 hover:shadow-md hover:scale-105 hover:-translate-y-1 border border-good-line group">
          <StatWithTooltip
            value={stats.newLeadsThisMonth.toString()}
            label="New This Month"
            tooltip="Number of leads added this month"
            valueClassName="text-2xl font-bold text-foreground dark:text-ink-faint"
            icon={<TrendingUp className="w-5 h-5 text-good" />}
          />
        </div>

        <div className="relative bg-secondary/20 p-6 rounded-lg shadow-sm transition-all duration-300 hover:shadow-md hover:scale-105 hover:-translate-y-1 border border-border-border group">
          <StatWithTooltip
            value={stats.leadsWithEmail.toString()}
            label="With Email"
            tooltip="Leads with email addresses"
            valueClassName="text-2xl font-bold text-foreground dark:text-ink-faint"
            icon={<Mail className="w-5 h-5 text-foreground" />}
          />
        </div>

        <div className="relative bg-mid-bg-mid-bg/20 p-6 rounded-lg shadow-sm transition-all duration-300 hover:shadow-md hover:scale-105 hover:-translate-y-1 border border-mid-line group">
          <StatWithTooltip
            value={stats.leadsWithoutEmail.toString()}
            label="Without Email"
            tooltip="Leads without email addresses"
            valueClassName="text-2xl font-bold text-foreground dark:text-ink-faint"
            icon={<MailX className="w-5 h-5 text-mid" />}
          />
        </div>

        <div className="relative bg-danger-bg-danger-bg/20 p-6 rounded-lg shadow-sm transition-all duration-300 hover:shadow-md hover:scale-105 hover:-translate-y-1 border border-danger-line group">
          <StatWithTooltip
            value={stats.scheduledLeads.toString()}
            label="Scheduled Calls"
            tooltip="Leads with scheduled calls"
            valueClassName="text-2xl font-bold text-foreground dark:text-ink-faint"
            icon={<Calendar className="w-5 h-5 text-danger" />}
          />
        </div>
      </div>

      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        {/* Only show search when loading or when there's data */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="relative flex items-center">
                <Search
                  className="absolute left-3 text-muted-foreground"
                  size={18}
                />
                <Input
                  placeholder="Search leads..."
                  value={localSearchValue}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10 w-full max-w-md"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="scheduled-filter"
                  checked={showScheduledOnly}
                  onCheckedChange={(checked) => {
                    setShowScheduledOnly(checked);
                    setSearchParams(
                      (prev) => {
                        const next = new URLSearchParams(prev);
                        if (checked) next.set("scheduledOnly", "true");
                        else next.delete("scheduledOnly");
                        return next;
                      },
                      { replace: true }
                    );
                  }}
                />
                <Label
                  htmlFor="scheduled-filter"
                  className="text-sm font-medium"
                >
                  Show scheduled only
                </Label>
              </div>
            </div>
            {isRefreshing && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <LoadingSpinner size="sm" />
                <span>Refreshing...</span>
              </div>
            )}
          </div>

        {loading && !isRefreshing ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner text="Loading leads..." />
          </div>
        ) : leads.length === 0 && !loading ? (
          <NoData
            icon={<UserRound className="h-12 w-12 text-muted-foreground/50" />}
            title="No leads found"
            description="Get started by creating your first lead. Leads help you keep track of potential customers."
            actionButton={
              <Button
                variant="primaryOutline"
                onClick={openCreateDialog}
                className="mt-2"
              >
                <Plus size={16} className="mr-2" />
                Create Your First Lead
              </Button>
            }
          />
        ) : filteredLeads.length === 0 ? (
          <NoData
            icon={<UserRound className="h-12 w-12 text-muted-foreground/50" />}
            title="No leads match your search"
            description="Try adjusting your search terms or clear the search to see all leads."
          />
        ) : (
          <>
            {/* Pagination Status */}
            <div className="flex justify-between items-center mb-4 text-sm text-muted-foreground">
              <span>
                {showScheduledOnly ? (
                  <span>Showing {filteredLeads.length} of {stats.scheduledLeads} scheduled leads</span>
                ) : pagination.method === 'cursor' ? (
                  <>
                    Showing {filteredLeads.length} leads
                    {pagination.estimatedTotal != null && (
                      <span> (approx. {pagination.estimatedTotal} total)</span>
                    )}
                  </>
                ) : (
                  <span>Showing {filteredLeads.length} of {pagination.total || 0} leads</span>
                )}
              </span>
            </div>
            <div className="overflow-x-auto">
              <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      if (sortField === "name") {
                        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                      } else {
                        setSortField("name");
                        setSortOrder("asc");
                      }
                    }}
                  >
                    <div className="flex items-center gap-1">
                      Name / Email
                      {sortField === "name" ? (
                        sortOrder === "asc" ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        )
                      ) : (
                        <ArrowUpDown className="h-4 w-4 opacity-50" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead>Phone Number</TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      if (sortField === "created_at") {
                        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                      } else {
                        setSortField("created_at");
                        setSortOrder("desc");
                      }
                    }}
                  >
                    <div className="flex items-center gap-1">
                      Created
                      {sortField === "created_at" ? (
                        sortOrder === "asc" ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        )
                      ) : (
                        <ArrowUpDown className="h-4 w-4 opacity-50" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      if (sortField === "scheduled_time") {
                        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                      } else {
                        setSortField("scheduled_time");
                        setSortOrder("asc");
                      }
                    }}
                  >
                    <div className="flex items-center gap-1">
                      Scheduled Call
                      {sortField === "scheduled_time" ? (
                        sortOrder === "asc" ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        )
                      ) : (
                        <ArrowUpDown className="h-4 w-4 opacity-50" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      if (sortField === "total_calls") {
                        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                      } else {
                        setSortField("total_calls");
                        setSortOrder("desc");
                      }
                    }}
                  >
                    <div className="flex items-center gap-1">
                      Total Calls
                      {sortField === "total_calls" ? (
                        sortOrder === "asc" ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        )
                      ) : (
                        <ArrowUpDown className="h-4 w-4 opacity-50" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      if (sortField === "last_called_at") {
                        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                      } else {
                        setSortField("last_called_at");
                        setSortOrder("desc");
                      }
                    }}
                  >
                    <div className="flex items-center gap-1">
                      Last Called At
                      {sortField === "last_called_at" ? (
                        sortOrder === "asc" ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        )
                      ) : (
                        <ArrowUpDown className="h-4 w-4 opacity-50" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <div className="font-medium">
                          {lead.first_name} {lead.last_name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {lead.email || "No email"}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <PhoneNumberCopy phoneNumber={lead.phone_number} />
                    </TableCell>
                    <TableCell>{formatDate(lead.created_at)}</TableCell>
                    <TableCell>
                      {lead.scheduledCall ? (
                        <div className="flex items-start gap-2">
                          <Clock size={14} className="text-foreground mt-0.5" />
                          <div className="flex flex-col gap-0.5">
                            <div className="text-foreground">
                              {formatDate(lead.scheduledCall.scheduled_time)}
                            </div>
                            <div className="text-sm text-foreground/80">
                              {new Date(
                                lead.scheduledCall.scheduled_time
                              ).toLocaleTimeString("en-US", {
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: true,
                              })}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-ink-faint">Not scheduled</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {lead.totalCalls || 0}
                    </TableCell>
                    <TableCell>
                      {lead.lastCalledAt ? (
                        <div className="flex flex-col gap-0.5">
                          <div>{formatDate(lead.lastCalledAt)}</div>
                          <div className="text-sm text-muted-foreground">
                            {new Date(lead.lastCalledAt).toLocaleTimeString(
                              "en-US",
                              {
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: true,
                              }
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-ink-faint">Never called</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <div className="flex gap-1"> 
                          <Button
                            variant="primaryOutline"
                            size="icon"
                            onClick={() => openCallDialog(lead)}
                            title="Voice Call"
                          >
                            <Phone size={16} />
                          </Button>
                          {videoV1 && (
                            <Button
                              variant="primaryOutline"
                              size="icon"
                              onClick={() => openCallDialog(lead, true)}
                              title="Video Interview"
                            >
                              <Video size={16} />
                            </Button>
                          )}
                        </div>
                        {!lead.scheduledCall ? (
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => openScheduleDialog(lead, "schedule")}
                            title="Schedule Call"
                          >
                            <Calendar size={16} className="text-good" />
                          </Button>
                        ) : (
                          <>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() =>
                                openScheduleDialog(lead, "reschedule")
                              }
                              title="Reschedule Call"
                            >
                              <Calendar size={16} className="text-mid" />
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => openCancelDialog(lead)}
                              title="Cancel Scheduled Call"
                            >
                              <Clock size={16} className="text-danger" />
                            </Button>
                          </>
                        )}
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            setSelectedLead(lead);
                            setIsWhatsAppDialogOpen(true);
                          }}
                          title="Send WhatsApp Message"
                        >
                          <MessageSquare size={16} className="text-good" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => openEditDialog(lead)}
                          title="Edit"
                        >
                          <Pencil size={16} className="text-foreground" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => openDeleteDialog(lead)}
                          title="Delete"
                        >
                          <Trash2 size={16} className="text-danger" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              </Table>
            </div>
            {pagination.hasMore && (!showScheduledOnly || filteredLeads.length < stats.scheduledLeads) && (
              <div className="flex justify-center mt-6">
                <Button
                  variant="outline"
                  onClick={() => fetchLeads(false, true)}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Loading more leads...
                    </>
                  ) : (
                    "Load More Leads"
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Lead Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={handleCreateDialogClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Lead</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) =>
                    setFormData({ ...formData, first_name: e.target.value })
                  }
                  placeholder="John"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) =>
                    setFormData({ ...formData, last_name: e.target.value })
                  }
                  placeholder="Doe"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="john.doe@example.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone_number">Phone Number *</Label>
              <PhoneInput
                id="phone_number"
                value={formData.phone_number}
                onChange={(value) =>
                  setFormData({ ...formData, phone_number: value })
                }
                placeholder="(555) 123-4567"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primaryOutline"
              onClick={handleCreateLead}
              isLoading={isSaving}
              loadingText="Creating..."
            >
              Create Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Lead Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={handleEditDialogClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Lead</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_first_name">First Name *</Label>
                <Input
                  id="edit_first_name"
                  value={formData.first_name}
                  onChange={(e) =>
                    setFormData({ ...formData, first_name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_last_name">Last Name *</Label>
                <Input
                  id="edit_last_name"
                  value={formData.last_name}
                  onChange={(e) =>
                    setFormData({ ...formData, last_name: e.target.value })
                  }
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_email">Email</Label>
              <Input
                id="edit_email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_phone_number">Phone Number *</Label>
              <PhoneInput
                id="edit_phone_number"
                value={formData.phone_number}
                onChange={(value) =>
                  setFormData({ ...formData, phone_number: value })
                }
                placeholder="(555) 123-4567"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleEditDialogClose(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primaryOutline"
              onClick={handleUpdateLead}
              isLoading={isSaving}
              loadingText="Updating..."
            >
              Update Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the lead for{" "}
              {currentLead?.first_name} {currentLead?.last_name}. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLead}
              className="bg-destructive hover:bg-destructive"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Call Confirmation Dialog */}
      <Dialog open={isCallDialogOpen} onOpenChange={(open) => {
        setIsCallDialogOpen(open);
        if (!open && isVideoCall) {
          // Reset dialog state when closing
          setDialogStep('assistant');
          setTabChangeDetection(false);
          setExternalMonitorDetection(false);
          setEnableScreenSharing(false);
          setEnableIntermittentFaceOutOfFocus(false);
          setGeneratedVideoLink("");
          setStartTime("");
        }
      }}>
        <DialogContent className="w-[90vw]">
          <DialogHeader>
            <DialogTitle>
              {isVideoCall 
                ? (dialogStep === 'assistant' ? "Select Assistant for Video Call" : 
                   dialogStep === 'monitoring' ? "Monitoring Options" : "Video Call Link")
                : "Initiate Call"}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex items-center space-x-3">
              {isVideoCall ? (
                <Video className="h-5 w-5 text-primary" />
              ) : (
                <Phone className="h-5 w-5 text-primary" />
              )}
              <span className="font-medium flex items-center gap-2">
                {leadToCall?.name} -
                {leadToCall?.phoneNumber && (
                  <PhoneNumberDisplay phoneNumber={leadToCall.phoneNumber} />
                )}
              </span>
            </div>

            {isVideoCall && dialogStep === 'link' ? (
              // Link generation step
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Share this link with {leadToCall?.name} to start the video interview.
                </p>
                <div className="flex gap-2">
                  <Input
                    value={generatedVideoLink}
                    readOnly
                    className="flex-1"
                    placeholder="Click 'Generate Link' to create the video call link"
                  />
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (generatedVideoLink) {
                        navigator.clipboard.writeText(generatedVideoLink);
                        toast({
                          title: "Copied!",
                          description: "Video call link copied to clipboard",
                        });
                      }
                    }}
                    disabled={!generatedVideoLink}
                  >
                    Copy
                  </Button>
                </div>
                {generatedVideoLink && (
                  <div className="text-sm text-muted-foreground space-y-2">
                    <p>This link includes:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Selected AI assistant configuration</li>
                      <li>Pre-filled template variables</li>
                      <li>Monitoring settings</li>
                    </ul>
                  </div>
                )}
              </div>
            ) : isVideoCall && dialogStep === 'monitoring' ? (
              // Monitoring options for video calls
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="tab-change">Tab Change Detection</Label>
                      <p className="text-sm text-muted-foreground">
                        Detect when the user switches browser tabs during the call
                      </p>
                    </div>
                    <Switch
                      id="tab-change"
                      checked={tabChangeDetection}
                      onCheckedChange={setTabChangeDetection}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="external-monitor">External Monitor Detection</Label>
                      <p className="text-sm text-muted-foreground">
                        Detect and report external monitors connected to the system
                      </p>
                    </div>
                    <Switch
                      id="external-monitor"
                      checked={externalMonitorDetection}
                      onCheckedChange={setExternalMonitorDetection}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="screen-sharing">Enable Screen Sharing</Label>
                      <p className="text-sm text-muted-foreground">
                        Require continuous screen sharing throughout the session
                      </p>
                    </div>
                    <Switch
                      id="screen-sharing"
                      checked={enableScreenSharing}
                      onCheckedChange={setEnableScreenSharing}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="start-time">Start Time (Optional)</Label>
                    <Input
                      id="start-time"
                      type="datetime-local"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      placeholder="When should the link become active?"
                    />
                    <p className="text-sm text-muted-foreground">
                      If set, the video link will only be accessible from this time. Leave empty for immediate access.
                    </p>
                  </div>

                </div>
              </div>
            ) : (
              // Assistant selection (original content)
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="assistant">Call Method</Label>
                <div className="grid grid-cols-1 gap-2">
                  <select
                    id="assistant"
                    className="w-full p-2 border border-input rounded-md bg-background"
                    value={selectedAssistantId || ""}
                    onChange={(e) => {
                      const newAssistantId = e.target.value || null;
                      setSelectedAssistantId(newAssistantId);
                      saveLastAssistantId(newAssistantId);

                      // Find the lead to reinitialize variables
                      if (newAssistantId && leadToCall) {
                        const currentLead = leads.find(
                          (lead) =>
                            `${lead.first_name} ${lead.last_name}` ===
                              leadToCall.name &&
                            lead.phone_number === leadToCall.phoneNumber
                        );

                        if (currentLead) {
                          // Initialize variables using the reusable function with the new assistant ID
                          initializeVariableValues(currentLead, newAssistantId);
                        }
                      } else {
                        setVariableValues({});
                      }
                    }}
                    disabled={loadingAssistants || initiatingCall}
                  >
                    <option value="">Manual Phone Call</option>
                    {loadingAssistants ? (
                      <option disabled>Loading assistants...</option>
                    ) : (
                      <>
                        <option disabled>─── AI Assistants ───</option>
                        {assistants.map((assistant) => (
                          <option key={assistant.id} value={assistant.id}>
                            {assistant.name}{" "}
                            {assistant.variables &&
                            assistant.variables.length > 0
                              ? `(${assistant.variables.length} variables)`
                              : ""}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>

                {selectedAssistantId && (
                  <div className="text-sm text-muted-foreground mt-2">
                    <p>
                      An AI agent will call the lead and handle the conversation
                      according to the assistant's configuration.
                    </p>
                  </div>
                )}

                {!selectedAssistantId && (
                  <div className="text-sm text-muted-foreground mt-2">
                    <p>
                      Your default phone app will open to make a manual call.
                    </p>
                  </div>
                )}

                {/* Display variables if the selected assistant has any */}
                {selectedAssistantId &&
                  getSelectedAssistantVariables().length > 0 && (
                    <div className="mt-4 border border-border rounded-md p-3">
                      <Label className="mb-2 block">Template Variables</Label>
                      <div className="grid grid-cols-3 gap-4">
                        {getSelectedAssistantVariables().map((variable) => (
                          <div key={variable} className="space-y-1">
                            <Label
                              htmlFor={`var-${variable}`}
                              className="text-xs text-muted-foreground"
                            >
                              {variable}
                            </Label>
                            <Input
                              id={`var-${variable}`}
                              value={variableValues[variable] || ""}
                              onChange={(e) =>
                                setVariableValues((prev) => ({
                                  ...prev,
                                  [variable]: e.target.value,
                                }))
                              }
                              placeholder={`Value for ${variable}`}
                              className="h-8 text-sm"
                            />
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        These values will replace {"{{variable}}"} placeholders
                        in the assistant's prompts
                      </p>
                    </div>
                  )}
              </div>

              {selectedAssistantId && !isVideoCall && (
                <div className="space-y-2">
                  <Label>Backend</Label>
                  <BackendPicker
                    value={selectedCallBackend}
                    onChange={setSelectedCallBackend}
                    disabled={initiatingCall}
                  />
                </div>
              )}

              {selectedAssistantId &&
                !isVideoCall &&
                selectedCallBackend === "v1" && (
                <div className="space-y-2">
                  <Label htmlFor="phone-number">Outbound Phone Number</Label>
                  <div className="grid grid-cols-1 gap-2">
                    <select
                      id="phone-number"
                      className="w-full p-2 border border-input rounded-md bg-background"
                      value={selectedPhoneNumberId || ""}
                      onChange={(e) =>
                        setSelectedPhoneNumberId(e.target.value || null)
                      }
                      disabled={loadingPhoneNumbers || initiatingCall}
                    >
                      <option value="">Select an outbound number</option>
                      {loadingPhoneNumbers ? (
                        <option disabled>Loading phone numbers...</option>
                      ) : phoneNumbers.length === 0 ? (
                        <option disabled>
                          No outbound phone numbers available
                        </option>
                      ) : (
                        phoneNumbers.map((phoneNumber) => (
                          <option key={phoneNumber.id} value={phoneNumber.id}>
                            {phoneNumber.phone_number}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  <div className="text-sm text-muted-foreground mt-2">
                    <p>
                      Select which phone number to use for making this outbound
                      call.
                    </p>
                  </div>

                  {phoneNumbers.length === 0 && (
                    <div className="text-sm text-mid mt-2">
                      <p>
                        No outbound phone numbers are configured. Please add a
                        phone number in the Phone Numbers settings.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (isVideoCall && dialogStep === 'link') {
                  // Go back to monitoring options
                  setDialogStep('monitoring');
                  setGeneratedVideoLink("");
                } else if (isVideoCall && dialogStep === 'monitoring') {
                  // Go back to assistant selection
                  setDialogStep('assistant');
                } else {
                  // Close dialog
                  setIsCallDialogOpen(false);
                }
              }}
              disabled={initiatingCall}
            >
              {isVideoCall && (dialogStep === 'monitoring' || dialogStep === 'link') ? "Back" : "Cancel"}
            </Button>
            <Button
              onClick={() => {
                if (isVideoCall && dialogStep === 'assistant' && selectedAssistantId) {
                  // Move to monitoring options step
                  setDialogStep('monitoring');
                } else if (isVideoCall && dialogStep === 'monitoring') {
                  // Generate the link
                  generateVideoCallLink();
                } else if (!isVideoCall || dialogStep === 'link') {
                  // Proceed with call or start video call
                  handleCall();
                }
              }}
              variant={selectedAssistantId ? "default" : "primaryOutline"}
              isLoading={initiatingCall || isGeneratingLink}
              loadingText={isGeneratingLink ? "Generating Link..." : (isVideoCall ? "Initiating Video Call..." : "Initiating Call...")}
              disabled={
                (isVideoCall && dialogStep === 'assistant' && !selectedAssistantId) ||
                (!isVideoCall &&
                  selectedAssistantId &&
                  selectedCallBackend === "v1" &&
                  !selectedPhoneNumberId) ||
                isGeneratingLink
              }
            >
              {isVideoCall && dialogStep === 'assistant' 
                ? "Next" 
                : isVideoCall && dialogStep === 'monitoring'
                  ? "Generate Link"
                  : isVideoCall && dialogStep === 'link' && generatedVideoLink
                    ? "Start Video Call Now"
                : selectedAssistantId 
                  ? (isVideoCall ? "Start Video Call" : "Call with AI Assistant") 
                  : "Call Manually"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Simplified Confirm Call Dialog */}
      {leadToCall && (
        <ConfirmCallDialog
          open={isConfirmCallDialogOpen}
          onOpenChange={setIsConfirmCallDialogOpen}
          leadName={leadToCall.name}
          phoneNumber={leadToCall.phoneNumber}
          agentName={
            assistants.find((a) => a.id === selectedAssistantId)?.name ||
            "AI Assistant"
          }
          variables={variableValues}
          backend={selectedCallBackend}
          onBackendChange={setSelectedCallBackend}
          assistantId={selectedAssistantId}
          startLanguage={startLanguage}
          onStartLanguageChange={setStartLanguage}
          onConfirm={handleCall}
        />
      )}

      {/* Schedule Call Dialog */}
      {selectedLead && (
        <ScheduleLeadCallDialog
          isOpen={isScheduleDialogOpen}
          onClose={() => setIsScheduleDialogOpen(false)}
          lead={selectedLead}
          onScheduled={fetchLeads}
          mode={scheduleMode}
          existingScheduledTime={selectedLead.scheduledCall?.scheduled_time}
          existingCallRequestId={selectedLead.scheduledCall?.id}
        />
      )}

      {/* Cancel Call Dialog */}
      {selectedLead && selectedLead.scheduledCall && (
        <CancelLeadCallDialog
          isOpen={isCancelDialogOpen}
          onClose={() => setIsCancelDialogOpen(false)}
          callRequestId={selectedLead.scheduledCall.id}
          leadName={`${selectedLead.first_name} ${selectedLead.last_name}`}
          onCancelled={fetchLeads}
        />
      )}

      {/* WhatsApp Message Dialog */}
      {selectedLead && isWhatsAppDialogOpen && currentOrganization && (
        <WhatsAppMessageDialog
          isOpen={isWhatsAppDialogOpen}
          onClose={() => setIsWhatsAppDialogOpen(false)}
          lead={selectedLead}
          organizationId={currentOrganization.id}
        />
      )}

      {/* Call Error Dialog */}
      <CallErrorDialog
        isOpen={isCallErrorDialogOpen}
        onOpenChange={setIsCallErrorDialogOpen}
        errorMessage={callErrorMessage}
        onGoToProviders={() => navigate('/providers')}
        onRetry={() => {
          // Retry the last call attempt
          setIsCallErrorDialogOpen(false);
          // Could trigger the same call again, but for now just close the dialog
        }}
      />
    </div>
  );
});


LeadsList.displayName = "LeadsList";


export default LeadsList;
